<template>
  <div class="flex h-screen bg-[#F8FAFC] font-sans text-slate-900 overflow-hidden">
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&display=swap" rel="stylesheet">

    <!-- Sidebar Gauche -->
    <aside class="w-72 bg-white border-r border-slate-200 flex flex-col z-20 shadow-sm">
      <div class="p-6 border-b border-slate-100">
        <h1 class="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Family Pack</h1>
        <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Écran de choix</p>
        <div class="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
          <p class="text-[10px] text-green-700 font-semibold">💡 Design partagé pour toutes les langues</p>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-4 space-y-6">

        <!-- Templates Prédéfinis -->
        <div>
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">⚡ Démarrage Rapide</h3>
          <div class="grid grid-cols-2 gap-2 px-2">
            <button
              v-for="(template, key) in templates"
              :key="key"
              @click="applyTemplate(key)"
              class="p-3 bg-gradient-to-br from-slate-50 to-slate-100 hover:from-green-50 hover:to-emerald-50 rounded-xl text-left transition-all border border-slate-200 hover:border-green-300 group"
            >
              <div class="text-2xl mb-1">{{ template.emoji }}</div>
              <div class="text-[10px] font-bold text-slate-700 group-hover:text-green-700">{{ template.name }}</div>
            </button>
          </div>
        </div>

        <!-- Palettes de Couleurs -->
        <div class="pt-4 border-t">
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">🎨 Palettes</h3>
          <div class="space-y-2 px-2">
            <button
              v-for="(palette, key) in colorPalettes"
              :key="key"
              @click="applyColorPalette(key)"
              class="w-full p-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-left transition-all border border-slate-200 hover:border-green-300 flex items-center gap-2"
            >
              <span class="text-lg">{{ palette.emoji }}</span>
              <span class="text-[10px] font-semibold text-slate-700 flex-1">{{ palette.name }}</span>
              <div class="flex gap-1">
                <div
                  v-for="(color, idx) in palette.colors.slice(0, 3)"
                  :key="idx"
                  class="w-4 h-4 rounded-full border border-white shadow-sm"
                  :style="{ backgroundColor: color }"
                ></div>
              </div>
            </button>
          </div>
        </div>

        <!-- Canvas Background Settings -->
        <div class="pt-4 border-t">
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">Fond du Canvas</h3>
          <div class="space-y-3 px-2">
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Type de fond</label>
              <select v-model="canvasBackgroundType" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="color">Couleur unie</option>
                <option value="gradient">Dégradé</option>
                <option value="image">Image</option>
              </select>
            </div>

            <div v-if="canvasBackgroundType === 'color'">
              <label class="text-[10px] text-slate-500 mb-1 block">Couleur de fond</label>
              <input type="color" v-model="currentLanguageConfig.branding.colors.background" class="w-full h-10 rounded cursor-pointer" />
            </div>

            <div v-if="canvasBackgroundType === 'gradient'" class="space-y-2">
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Couleur 1</label>
                <input type="color" v-model="gradientColor1" class="w-full h-8 rounded cursor-pointer" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Couleur 2</label>
                <input type="color" v-model="gradientColor2" class="w-full h-8 rounded cursor-pointer" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Direction</label>
                <select v-model="gradientDirection" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                  <option value="to right">Horizontal →</option>
                  <option value="to bottom">Vertical ↓</option>
                  <option value="to bottom right">Diagonal ↘</option>
                </select>
              </div>
            </div>

            <div v-if="canvasBackgroundType === 'image'">
              <label class="text-[10px] text-slate-500 mb-1 block">Image de fond</label>
              <MediaPicker
                v-model="canvasBackgroundImage"
                :agencyId="agencyId"
                buttonText="Choisir l'image"
              />
              <label class="text-[10px] text-slate-500 mb-1 block mt-2">Ajustement</label>
              <select v-model="canvasBackgroundSize" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="cover">Couvrir</option>
                <option value="contain">Contenir</option>
                <option value="auto">Auto</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Éléments sur la scène -->
        <div class="pt-4 border-t">
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">Éléments sur la scène</h3>
          <div class="space-y-1 px-2">
            <button
              v-for="(block, bIdx) in currentBlocks"
              :key="block.id"
              @click="selectBlock(bIdx)"
              :class="['w-full text-left px-3 py-2 rounded-lg text-xs transition-all',
                       selectedBlockIndex === bIdx ? 'bg-green-500 text-white' : 'bg-slate-50 hover:bg-slate-100']"
            >
              <span class="mr-2">{{ getBlockIcon(block.type) }}</span>
              <span>{{ getBlockLabel(block) }}</span>
            </button>
          </div>

          <button @click="showAddBlockMenu = true" class="w-full mt-3 py-2 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 text-xs font-bold hover:border-green-400 hover:text-green-500 transition-all">
            + Ajouter un élément
          </button>
        </div>
      </div>

      <div class="p-4 border-t border-slate-100 space-y-2">
        <button @click="saveConfig" class="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold shadow-xl hover:bg-green-600 transition-all flex items-center justify-center gap-2">
          <span>🚀 Publier</span>
        </button>
      </div>
    </aside>

    <!-- Canvas Preview -->
    <main class="flex-1 relative bg-slate-100 overflow-hidden p-12 flex justify-center items-center">
      <div class="relative bg-slate-800 p-4 rounded-[4rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.4)] border-[8px] border-slate-700">
        <div
          id="canvas"
          class="relative overflow-hidden bg-white w-[375px] h-[667px] rounded-[2rem] shadow-inner"
          :style="getCanvasBackgroundStyle()"
          @mousemove="handleDrag"
          @mouseup="stopDrag"
        >
          <!-- Render blocks -->
          <div
            v-for="(block, bIdx) in currentBlocks"
            :key="block.id"
            :style="getBlockStyle(block)"
            @mousedown="startDrag($event, bIdx)"
            :class="['absolute cursor-move', selectedBlockIndex === bIdx ? 'ring-2 ring-green-500' : '']"
          >
            <!-- Text Block -->
            <div v-if="block.type === 'text'"
                 :style="{
                   fontSize: (block.fontSize / SCALE_FACTOR) + 'px',
                   fontFamily: block.fontFamily,
                   fontWeight: block.fontWeight,
                   color: block.color,
                   textAlign: block.textAlign,
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: block.textAlign === 'center' ? 'center' : (block.textAlign === 'right' ? 'flex-end' : 'flex-start'),
                   padding: '8px',
                   width: '100%',
                   height: '100%',
                   overflow: 'hidden'
                 }">
              {{ block.text }}
            </div>

            <!-- Image Block -->
            <img v-else-if="block.type === 'image'"
                 :src="block.icon || block.src"
                 :style="{ width: '100%', height: '100%', objectFit: block.fit || 'contain' }"
                 @error="$event.target.src = 'https://via.placeholder.com/400x120?text=Image+Error'"
                 alt="Block image" />

            <!-- Family Pack Button Block -->
            <div v-else-if="block.type === 'family_pack_button'"
                 :style="{
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   fontSize: (block.fontSize / SCALE_FACTOR) + 'px',
                   fontFamily: block.fontFamily,
                   fontWeight: block.fontWeight,
                   color: block.color,
                   width: '100%',
                   height: '100%',
                   textAlign: 'center',
                   padding: '8px'
                 }">
              {{ block.label }}
            </div>

            <!-- Button Block -->
            <div v-else-if="block.type === 'button'"
                 :style="{
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   fontSize: (block.fontSize / SCALE_FACTOR) + 'px',
                   fontFamily: block.fontFamily,
                   fontWeight: block.fontWeight,
                   color: block.color,
                   width: '100%',
                   height: '100%',
                   textAlign: 'center',
                   padding: '8px'
                 }">
              {{ block.text }}
            </div>

            <!-- Video Block -->
            <iframe v-else-if="block.type === 'video'"
                    :src="block.src || block.url"
                    style="width: 100%; height: 100%; border: none;" />

            <!-- Shape Blocks -->
            <div v-else-if="block.type === 'triangle'"
                 :style="{
                   width: 0,
                   height: 0,
                   borderLeft: (block.width / SCALE_FACTOR / 2) + 'px solid transparent',
                   borderRight: (block.width / SCALE_FACTOR / 2) + 'px solid transparent',
                   borderBottom: (block.height / SCALE_FACTOR) + 'px solid ' + (block.backgroundColor || '#10B981')
                 }">
            </div>

            <div v-else style="width: 100%; height: 100%;"></div>
          </div>
        </div>
      </div>
    </main>

    <!-- Sidebar Droite - Property Panel -->
    <aside class="w-80 bg-white border-l border-slate-200 flex flex-col z-20 shadow-xl overflow-hidden">
      <div v-if="selectedBlock" class="flex-1 overflow-y-auto p-6 space-y-6">
        <div class="flex items-center justify-between pb-4 border-b">
          <h2 class="text-sm font-bold text-slate-800">Propriétés</h2>
          <button @click="clearSelection" class="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <!-- Position & Size -->
        <div class="space-y-3">
          <h3 class="text-xs font-bold text-slate-400 uppercase">Position & Taille</h3>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="text-[10px] text-slate-500">X</label>
              <input type="number" v-model.number="selectedBlock.x" @input="updateCanvas" class="w-full p-2 bg-slate-50 rounded border text-xs" />
            </div>
            <div>
              <label class="text-[10px] text-slate-500">Y</label>
              <input type="number" v-model.number="selectedBlock.y" @input="updateCanvas" class="w-full p-2 bg-slate-50 rounded border text-xs" />
            </div>
            <div>
              <label class="text-[10px] text-slate-500">Largeur</label>
              <input type="number" v-model.number="selectedBlock.width" @input="updateCanvas" class="w-full p-2 bg-slate-50 rounded border text-xs" />
            </div>
            <div>
              <label class="text-[10px] text-slate-500">Hauteur</label>
              <input type="number" v-model.number="selectedBlock.height" @input="updateCanvas" class="w-full p-2 bg-slate-50 rounded border text-xs" />
            </div>
          </div>
        </div>

        <!-- Text Properties -->
        <div v-if="selectedBlock.type === 'text' || selectedBlock.type === 'button' || selectedBlock.type === 'family_pack_button'" class="space-y-3">
          <h3 class="text-xs font-bold text-slate-400 uppercase">Texte</h3>
          <div v-if="selectedBlock.type !== 'family_pack_button'">
            <label class="text-[10px] text-slate-500">Contenu</label>
            <textarea v-model="selectedBlock.text" @input="updateCanvas" class="w-full p-2 bg-slate-50 rounded border text-xs" rows="3"></textarea>
          </div>
          <div v-if="selectedBlock.type === 'family_pack_button'">
            <label class="text-[10px] text-slate-500">Label du bouton</label>
            <input type="text" v-model="selectedBlock.label" @input="updateCanvas" class="w-full p-2 bg-slate-50 rounded border text-xs" />
            <label class="text-[10px] text-slate-500 mt-2 block">Valeur de choix</label>
            <select v-model="selectedBlock.choiceValue" @change="updateFamilyPackChoice" class="w-full p-2 bg-slate-50 rounded border text-xs">
              <option :value="true">Accepte Family Pack</option>
              <option :value="false">Refuse Family Pack</option>
            </select>
          </div>
          <div>
            <label class="text-[10px] text-slate-500">Taille de police</label>
            <input type="number" v-model.number="selectedBlock.fontSize" @input="updateCanvas" class="w-full p-2 bg-slate-50 rounded border text-xs" />
          </div>
          <div>
            <label class="text-[10px] text-slate-500">Couleur</label>
            <input type="color" v-model="selectedBlock.color" @input="updateCanvas" class="w-full h-10 rounded cursor-pointer" />
          </div>
        </div>

        <!-- Image Properties -->
        <div v-if="selectedBlock.type === 'image'" class="space-y-3">
          <h3 class="text-xs font-bold text-slate-400 uppercase">Image</h3>
          <div>
            <label class="text-[10px] text-slate-500 mb-2 block">Source de l'image</label>
            <MediaPicker
              v-model="selectedBlock.src"
              :agencyId="agencyId"
              buttonText="Choisir une image"
              @update:modelValue="updateCanvas"
            />
          </div>
          <div>
            <label class="text-[10px] text-slate-500">Ajustement</label>
            <select v-model="selectedBlock.fit" @change="updateCanvas" class="w-full p-2 bg-slate-50 rounded border text-xs">
              <option value="contain">Contenir</option>
              <option value="cover">Couvrir</option>
              <option value="fill">Remplir</option>
              <option value="none">Aucun</option>
            </select>
          </div>
        </div>

        <!-- Style Properties -->
        <div class="space-y-3">
          <h3 class="text-xs font-bold text-slate-400 uppercase">Style</h3>
          <div>
            <label class="text-[10px] text-slate-500">Couleur de fond</label>
            <input type="color" v-model="selectedBlock.backgroundColor" @input="updateCanvas" class="w-full h-10 rounded cursor-pointer" />
          </div>
          <div>
            <label class="text-[10px] text-slate-500">Arrondi (px)</label>
            <input type="number" v-model.number="selectedBlock.borderRadius" @input="updateCanvas" class="w-full p-2 bg-slate-50 rounded border text-xs" />
          </div>
        </div>

        <button @click="deleteBlock" class="w-full py-2 bg-red-500 text-white rounded-lg text-xs font-semibold hover:bg-red-600 transition-all">
          🗑️ Supprimer
        </button>
      </div>

      <div v-else class="flex-1 flex flex-col items-center justify-center p-12 text-center text-slate-400">
        <div class="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-2xl mb-4 grayscale">🎯</div>
        <p class="text-xs font-semibold">Cliquez sur un élément du canvas pour le régler.</p>
      </div>
    </aside>

    <!-- Block Type Menu Modal -->
    <div v-if="showAddBlockMenu" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click="showAddBlockMenu = false">
      <div class="bg-white rounded-2xl p-6 w-96" @click.stop>
        <h2 class="text-lg font-bold mb-4">Ajouter un élément</h2>
        <div class="grid grid-cols-2 gap-2">
          <button @click="addBlockToCanvas('text')" class="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 text-sm">📝 Texte</button>
          <button @click="addBlockToCanvas('image')" class="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 text-sm">🖼️ Image</button>
          <button @click="addFamilyPackButton(true)" class="p-4 bg-green-50 rounded-lg hover:bg-green-100 text-sm">✅ Accepter</button>
          <button @click="addFamilyPackButton(false)" class="p-4 bg-red-50 rounded-lg hover:bg-red-100 text-sm">❌ Refuser</button>
          <button @click="addBlockToCanvas('button')" class="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 text-sm">🔘 Bouton</button>
          <button @click="addBlockToCanvas('rectangle')" class="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 text-sm">⬜ Rectangle</button>
          <button @click="addBlockToCanvas('circle')" class="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 text-sm">⭕ Cercle</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { toast } from "vue3-toastify";
import { useRoute } from 'vue-router';
import { TEMPLATES, COLOR_PALETTES } from '@/utils/familyPackTemplates';
import MediaPicker from '@/components/MediaPicker.vue';

// Configuration du canvas
const TARGET_WIDTH = 1080;
const EDITOR_WIDTH = 375;
const SCALE_FACTOR = TARGET_WIDTH / EDITOR_WIDTH;

const route = useRoute();
const agencyId = route.params.kioskId;

const showAddBlockMenu = ref(false);

// Templates et palettes
const templates = TEMPLATES;
const colorPalettes = COLOR_PALETTES;

// États pour le background du canvas
const canvasBackgroundType = ref('color');
const gradientColor1 = ref('#667eea');
const gradientColor2 = ref('#764ba2');
const gradientDirection = ref('to right');
const canvasBackgroundImage = ref('');
const canvasBackgroundSize = ref('cover');

const config = ref({
  agencyId: agencyId,
  version: 2.0,
  enabled: false,
  buttonTexts: {
    acceptText: {
      fr: "Je profite de l'offre Family Pack et accepte les génériques",
      en: "I take advantage of the Family Pack offer and accept generics"
    },
    refuseText: {
      fr: "Je ne profite pas de l'offre Family Pack",
      en: "I do not take advantage of the Family Pack offer"
    }
  },
  languages: {
    fr: {
      branding: {
        colors: { background: '#F8FAFC' },
        canvasBackground: { type: 'color' }
      },
      layout: { screenPadding: 24 },
      containers: [{
        type: 'family_pack_canvas',
        display: 'absolute',
        blocks: []
      }]
    }
  },
  targetResolution: {
    width: 1080,
    height: 1920
  }
});

const currentLanguageConfig = computed(() => {
  return config.value.languages?.fr || null;
});

const currentBlocks = computed(() => {
  return currentLanguageConfig.value?.containers?.[0]?.blocks || [];
});

// Drag & Drop State
const isDragging = ref(false);
const selectedBlockIndex = ref(null);
const startMousePos = { x: 0, y: 0 };
const startBlockPos = { x: 0, y: 0 };

const selectedBlock = computed(() => {
  if (selectedBlockIndex.value === null) return null;
  return currentBlocks.value[selectedBlockIndex.value];
});

// Charger la configuration existante
onMounted(async () => {
  try {
    const token = localStorage.getItem('superAdminToken');
    const response = await fetch(`https://api.datafuse.fr/api/super-admin/family-pack-config/${agencyId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      const data = await response.json();
      config.value = data;

      // Synchroniser les états du background
      if (data.languages?.fr?.branding?.canvasBackground) {
        const bg = data.languages.fr.branding.canvasBackground;
        canvasBackgroundType.value = bg.type || 'color';
        if (bg.gradient) {
          gradientColor1.value = bg.gradient.color1 || '#667eea';
          gradientColor2.value = bg.gradient.color2 || '#764ba2';
          gradientDirection.value = bg.gradient.direction || 'to right';
        }
        if (bg.image) {
          canvasBackgroundImage.value = bg.image.url || '';
          canvasBackgroundSize.value = bg.image.size || 'cover';
        }
      }
    }
  } catch (error) {
    console.error('Erreur chargement config:', error);
  }
});

// Watch pour synchroniser le background
watch([canvasBackgroundType, gradientColor1, gradientColor2, gradientDirection, canvasBackgroundImage, canvasBackgroundSize], () => {
  if (!currentLanguageConfig.value) return;

  currentLanguageConfig.value.branding.canvasBackground.type = canvasBackgroundType.value;

  if (canvasBackgroundType.value === 'gradient') {
    currentLanguageConfig.value.branding.canvasBackground.gradient = {
      color1: gradientColor1.value,
      color2: gradientColor2.value,
      direction: gradientDirection.value
    };
  } else if (canvasBackgroundType.value === 'image') {
    currentLanguageConfig.value.branding.canvasBackground.image = {
      url: canvasBackgroundImage.value,
      size: canvasBackgroundSize.value
    };
  }
});

function getCanvasBackgroundStyle() {
  if (!currentLanguageConfig.value) return {};

  const bg = currentLanguageConfig.value.branding.canvasBackground;

  if (bg.type === 'gradient' && bg.gradient) {
    return {
      background: `linear-gradient(${bg.gradient.direction}, ${bg.gradient.color1}, ${bg.gradient.color2})`
    };
  } else if (bg.type === 'image' && bg.image?.url) {
    return {
      backgroundImage: `url(${bg.image.url})`,
      backgroundSize: bg.image.size || 'cover',
      backgroundPosition: 'center'
    };
  } else {
    return {
      backgroundColor: currentLanguageConfig.value.branding.colors.background
    };
  }
}

function getBlockStyle(block) {
  const style = {
    left: (block.x / SCALE_FACTOR) + 'px',
    top: (block.y / SCALE_FACTOR) + 'px',
    width: (block.width / SCALE_FACTOR) + 'px',
    height: (block.height / SCALE_FACTOR) + 'px',
    backgroundColor: block.backgroundColor || 'transparent',
    borderRadius: (block.borderRadius || 0) + 'px',
    opacity: block.opacity || 1
  };

  if (block.type === 'circle') {
    style.borderRadius = '50%';
  }

  return style;
}

function getBlockIcon(type) {
  const icons = {
    text: '📝',
    image: '🖼️',
    family_pack_button: '✅',
    button: '🔘',
    rectangle: '⬜',
    circle: '⭕',
    triangle: '🔺',
    video: '🎥'
  };
  return icons[type] || '❓';
}

function getBlockLabel(block) {
  if (block.type === 'text') return block.text || 'Texte';
  if (block.type === 'family_pack_button') return block.label || 'Bouton Family Pack';
  if (block.type === 'button') return block.text || 'Bouton';
  if (block.type === 'image') return 'Image';
  return block.type;
}

function selectBlock(index) {
  selectedBlockIndex.value = index;
}

function clearSelection() {
  selectedBlockIndex.value = null;
}

function addBlockToCanvas(type) {
  const newBlock = {
    id: `block_${Date.now()}`,
    type: type,
    x: 540, // Center in 1080px width
    y: 500,
    width: type === 'family_pack_button' ? 800 : 300,
    height: type === 'family_pack_button' ? 120 : 100,
    backgroundColor: type === 'family_pack_button' ? '#10B981' : '#FFFFFF',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#E5E7EB',
    opacity: 1
  };

  if (type === 'text' || type === 'button') {
    Object.assign(newBlock, {
      text: type === 'text' ? 'Nouveau texte' : 'Bouton',
      fontSize: 48,
      fontWeight: '600',
      fontFamily: 'Plus Jakarta Sans',
      textAlign: 'center',
      color: '#1F2937'
    });
  }

  if (type === 'family_pack_button') {
    Object.assign(newBlock, {
      label: "Je profite de l'offre Family Pack",
      choiceValue: true,
      fontSize: 42,
      fontWeight: '700',
      fontFamily: 'Plus Jakarta Sans',
      color: '#FFFFFF',
      action: {
        type: 'family_pack_choice',
        choiceValue: true
      }
    });
  }

  if (type === 'image') {
    newBlock.src = 'https://via.placeholder.com/400x120';
    newBlock.fit = 'contain';
  }

  currentLanguageConfig.value.containers[0].blocks.push(newBlock);
  showAddBlockMenu.value = false;
  selectedBlockIndex.value = currentBlocks.value.length - 1;
}

function deleteBlock() {
  if (selectedBlockIndex.value !== null) {
    currentLanguageConfig.value.containers[0].blocks.splice(selectedBlockIndex.value, 1);
    clearSelection();
  }
}

function startDrag(event, index) {
  isDragging.value = true;
  selectedBlockIndex.value = index;
  const block = currentBlocks.value[index];

  startMousePos.x = event.clientX;
  startMousePos.y = event.clientY;
  startBlockPos.x = block.x;
  startBlockPos.y = block.y;

  event.preventDefault();
}

function handleDrag(event) {
  if (!isDragging.value || selectedBlockIndex.value === null) return;

  const block = currentBlocks.value[selectedBlockIndex.value];
  const deltaX = (event.clientX - startMousePos.x) * SCALE_FACTOR;
  const deltaY = (event.clientY - startMousePos.y) * SCALE_FACTOR;

  block.x = Math.max(0, Math.min(1080 - block.width, startBlockPos.x + deltaX));
  block.y = Math.max(0, Math.min(1920 - block.height, startBlockPos.y + deltaY));
}

function stopDrag() {
  isDragging.value = false;
}

function updateCanvas() {
  // Force reactivity update
  config.value = { ...config.value };
}

function updateFamilyPackChoice() {
  // Synchroniser choiceValue dans l'objet et dans action
  if (selectedBlock.value && selectedBlock.value.type === 'family_pack_button') {
    if (!selectedBlock.value.action) {
      selectedBlock.value.action = { type: 'family_pack_choice' };
    }
    selectedBlock.value.action.choiceValue = selectedBlock.value.choiceValue;
    console.log('✅ ChoiceValue mis à jour:', selectedBlock.value.choiceValue);
  }
  updateCanvas();
}

function addFamilyPackButton(acceptChoice) {
  const newBlock = {
    id: `block_${Date.now()}`,
    type: 'family_pack_button',
    x: 540,
    y: acceptChoice ? 800 : 1000, // Positionner différemment selon le type
    width: 800,
    height: 120,
    backgroundColor: acceptChoice ? '#10B981' : '#EF4444', // Vert pour accepter, rouge pour refuser
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#E5E7EB',
    opacity: 1,
    label: acceptChoice ? "Je profite de l'offre Family Pack" : "Je ne profite pas de l'offre Family Pack",
    choiceValue: acceptChoice,
    fontSize: 42,
    fontWeight: '700',
    fontFamily: 'Plus Jakarta Sans',
    color: '#FFFFFF',
    action: {
      type: 'family_pack_choice',
      choiceValue: acceptChoice
    }
  };

  currentBlocks.value.push(newBlock);
  showAddBlockMenu.value = false;
  updateCanvas();
  console.log(`✅ Bouton Family Pack créé (${acceptChoice ? 'Accepter' : 'Refuser'}):`, newBlock);
}

// Appliquer un template prédéfini
function applyTemplate(templateKey) {
  const template = TEMPLATES[templateKey];
  if (!template) return;

  // Copier la configuration du template
  currentLanguageConfig.value.branding = JSON.parse(JSON.stringify(template.config.branding));
  currentLanguageConfig.value.containers = JSON.parse(JSON.stringify(template.config.containers));

  // Synchroniser les états du background
  const canvasBg = currentLanguageConfig.value.branding.canvasBackground;
  if (canvasBg) {
    canvasBackgroundType.value = canvasBg.type || 'color';

    if (canvasBg.type === 'gradient' && canvasBg.gradient) {
      gradientColor1.value = canvasBg.gradient.color1;
      gradientColor2.value = canvasBg.gradient.color2;
      gradientDirection.value = canvasBg.gradient.direction;
    }

    if (canvasBg.type === 'image' && canvasBg.image) {
      canvasBackgroundImage.value = canvasBg.image.url || '';
      canvasBackgroundSize.value = canvasBg.image.size || 'cover';
    }
  }

  selectedBlockIndex.value = -1;
  updateCanvas();
  toast.success(`Template "${template.name}" appliqué ! ${template.emoji}`);
}

// Appliquer une palette de couleurs
function applyColorPalette(paletteKey) {
  const palette = COLOR_PALETTES[paletteKey];
  if (!palette || !selectedBlock.value) {
    toast.warning('Sélectionnez un élément pour appliquer la palette');
    return;
  }

  // Appliquer la couleur principale au bloc sélectionné
  if (selectedBlock.value.type === 'family_pack_button' || selectedBlock.value.type === 'button') {
    selectedBlock.value.backgroundColor = palette.colors[0];

    // Si c'est un bouton "refuser", utiliser une couleur plus douce
    if (selectedBlock.value.choiceValue === false) {
      selectedBlock.value.backgroundColor = palette.colors[3];
      selectedBlock.value.color = palette.colors[0];
    }
  } else if (selectedBlock.value.type === 'text') {
    selectedBlock.value.color = palette.colors[0];
  } else {
    selectedBlock.value.backgroundColor = palette.colors[0];
  }

  updateCanvas();
  toast.success(`Palette "${palette.name}" appliquée ! ${palette.emoji}`);
}

async function saveConfig() {
  try {
    const token = localStorage.getItem('superAdminToken');
    const response = await fetch(`https://api.datafuse.fr/api/super-admin/family-pack-config/${agencyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(config.value)
    });

    if (response.ok) {
      toast.success('Configuration Family Pack enregistrée !');
    } else {
      toast.error('Erreur lors de la sauvegarde');
    }
  } catch (error) {
    console.error('Erreur sauvegarde:', error);
    toast.error('Erreur lors de la sauvegarde');
  }
}
</script>

<style scoped>
/* Styles additionnels si nécessaire */
</style>
